import { api, setSessionToken, clearSessionToken, getSessionUser, getSessionToken } from './api';

function getApiErrorDetail(error: unknown, fallback: string): string {
  const detail = (error as any)?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return error instanceof Error ? error.message : fallback;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface EntraExchangeRequest {
  access_token: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token?: string;
  user: {
    id: string;
    username: string;
    email?: string;
    role?: string;
  };
}

class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/auth/login', credentials);
      const data = response.data;

      const expiresAt = Date.now() + 25 * 60 * 1000; // 25 min buffer on ~30 min access token
      const refreshExpiresAt = Date.now() + 7.5 * 60 * 60 * 1000; // 7.5h buffer on ~8h refresh

      setSessionToken({
        token: data.access_token,
        userId: data.user.id,
        username: data.user.username,
        expiresAt,
        authProvider: 'local',
        refreshToken: data.refresh_token,
        refreshExpiresAt,
      });

      return data;
    } catch (error) {
      throw new Error(getApiErrorDetail(error, 'Login failed'));
    }
  }

  async loginWithEntra(payload: EntraExchangeRequest): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/auth/entra/exchange', payload);
      const data = response.data;

      const expiresAt = Date.now() + 25 * 60 * 1000;
      const refreshExpiresAt = Date.now() + 7.5 * 60 * 60 * 1000;

      setSessionToken({
        token: data.access_token,
        userId: data.user.id,
        username: data.user.username,
        expiresAt,
        authProvider: 'entra',
        refreshToken: data.refresh_token,
        refreshExpiresAt,
      });

      return data;
    } catch (error) {
      throw new Error(getApiErrorDetail(error, 'Entra login failed'));
    }
  }

  async logout(): Promise<void> {
    try {
      // Optionally notify backend of logout
      await api.post('/auth/logout');
    } catch {
      // Ignore errors on logout
    } finally {
      clearSessionToken();
    }
  }

  getCurrentUser() {
    return getSessionUser();
  }

  getToken() {
    return getSessionToken();
  }

  isAuthenticated(): boolean {
    return !!getSessionToken() && !!getSessionUser();
  }

  // Token renewal (can be called before expiry)
  async renewToken(): Promise<LoginResponse> {
    try {
      const existingSession = getSessionUser();
      const currentRefreshToken = existingSession?.refreshToken || localStorage.getItem('refresh_token') || undefined;
      const response = await api.post<LoginResponse>('/auth/refresh', {
        refresh_token: currentRefreshToken,
      });
      const data = response.data;

      const expiresAt = Date.now() + 25 * 60 * 1000;
      const refreshExpiresAt = Date.now() + 7.5 * 60 * 60 * 1000;

      setSessionToken({
        token: data.access_token,
        userId: data.user.id,
        username: data.user.username,
        expiresAt,
        authProvider: existingSession?.authProvider,
        // Refresh endpoint may only return a new access token.
        refreshToken: data.refresh_token || currentRefreshToken,
        refreshExpiresAt,
      });

      return data;
    } catch (error) {
      clearSessionToken();
      throw new Error(getApiErrorDetail(error, 'Token renewal failed'));
    }
  }
}

export default new AuthService();
